/**
 * Multi-Path client — browser-side caller for /api/multi-path
 *
 * Approximates the path integral Z = ∫D(responses)·exp(S) with two discrete
 * arms (sampler + evolved), scores each, and returns the winner.
 *
 * Architecture sources:
 * - AMOE Evolve Loop: two-arm propose/verify with shaped scoring
 * - MiniMax H3-Regenerate-2K: feed output back as context for refinement
 * - Standard Model Path Integral: sum over paths, not commit to one
 */

import { ChatMessage, Step } from './harness';

export interface Candidate {
  arm: 'sampler' | 'evolved';
  text: string;
  score: number;
  scores: {
    total: number;
    conciseness: number;
    grounding: number;
    on_topic: number;
    no_filler: number;
  };
  error?: string | null;
}

export interface MultiPathResult {
  answer: string;
  winner: 'sampler' | 'evolved';
  candidates: Candidate[];
}

export async function runMultiPath(
  messages: ChatMessage[],
  question: string,
  opts: {
    model: string;
    pageTitle?: string;
    pageContent?: string;
    signal?: AbortSignal;
    onStep?: (step: Step) => void;
  },
): Promise<MultiPathResult> {
  const { model, pageTitle, pageContent, signal, onStep } = opts;

  onStep?.({ kind: 'multi_path' as any, text: 'running path integral — sampler arm…' });

  try {
    const res = await fetch('/api/multi-path', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages,
        model,
        page_title: pageTitle,
        page_content: pageContent,
        question,
      }),
      signal,
    });

    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`multi-path ${res.status}${body ? `: ${body.slice(0, 120)}` : ''}`);
    }

    const data: MultiPathResult = await res.json();

    // Emit trace step with candidate comparison
    const best = data.candidates.find(c => c.arm === data.winner);
    const other = data.candidates.find(c => c.arm !== data.winner);
    onStep?.({
      kind: 'multi_path' as any,
      text: `winner: ${data.winner} (S=${best?.score?.toFixed(3)}) beat ${other?.arm} (S=${other?.score?.toFixed(3)})`,
      detail: { candidates: data.candidates, winner: data.winner },
    });

    return data;
  } catch (err: any) {
    onStep?.({ kind: 'error' as any, text: `multi-path failed: ${err?.message}` });
    throw err;
  }
}
